import { useState, useEffect, useRef } from 'react';
import styles from './AuditionsFilterBar.module.css';

const C = { bk: '#090909', white: '#ffffff', g200: '#E9E9EC', g300: '#D0D0D5', g400: '#888896', sub: '#888896', point: '#FF6B2C' };
const ff = "'Pretendard', sans-serif";
const t = (size, weight, color) => ({ fontFamily: ff, fontSize: size, fontWeight: weight, color, letterSpacing: '-0.04em' });

const GENRES = ['드라마', '영화', '웹드라마', 'CF·광고', '뮤지컬', '독립영화', '예능', '기타'];

const METRO_CITIES = ['인천', '부산', '대구', '대전', '광주', '울산', '세종'];

const REGION_DATA = [
  { province: '서울', cities: [] },
  { province: '광역시', cities: METRO_CITIES },
  { province: '경기', cities: ['수원', '성남', '고양', '용인', '부천', '안산', '안양', '남양주', '화성', '평택', '의정부', '시흥', '파주', '광명', '김포', '군포', '광주', '이천', '양주', '오산', '구리', '안성', '포천', '의왕', '하남', '여주', '동두천', '과천'] },
  { province: '강원', cities: ['춘천', '원주', '강릉', '동해', '태백', '속초', '삼척'] },
  { province: '충북', cities: ['청주', '충주', '제천'] },
  { province: '충남', cities: ['천안', '공주', '보령', '아산', '서산', '논산', '계룡', '당진'] },
  { province: '전북', cities: ['전주', '군산', '익산', '정읍', '남원', '김제'] },
  { province: '전남', cities: ['목포', '여수', '순천', '나주', '광양'] },
  { province: '경북', cities: ['포항', '경주', '김천', '안동', '구미', '영주', '영천', '상주', '문경', '경산'] },
  { province: '경남', cities: ['창원', '진주', '통영', '사천', '김해', '밀양', '거제', '양산'] },
  { province: '제주', cities: ['제주시', '서귀포'] },
];

function ChevronIcon({ up }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ transform: up ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
      <path d="M6 9l6 6 6-6" stroke={C.g400} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RegionAccordion({ region, selected, onToggle, isMetro }) {
  const [open, setOpen] = useState(false);

  if (isMetro) {
    // 광역시: 각 도시가 독립적인 지역 (서울, 부산 등)
    const selectedCount = region.cities.filter(c => selected.includes(c)).length;
    const allSelected = selectedCount === region.cities.length;
    const hasSelection = selectedCount > 0;

    return (
      <div style={{ borderRadius: '10px', border: `1px solid ${hasSelection ? C.bk : C.g200}`, overflow: 'hidden' }}>
        <button
          onClick={() => setOpen(p => !p)}
          style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: open ? '#f8f8f8' : C.white, border: 'none', cursor: 'pointer' }}
        >
          <span style={t('14px', 600, C.bk)}>
            광역시 / 특별시
            {hasSelection && (
              <span style={{ marginLeft: '6px', ...t('12px', 700, C.point) }}>{allSelected ? '전체' : selectedCount}</span>
            )}
          </span>
          <ChevronIcon up={open} />
        </button>
        {open && (
          <div style={{ padding: '14px', display: 'flex', flexWrap: 'wrap', gap: '6px', borderTop: `1px solid ${C.g200}` }}>
            <button onClick={() => {
              if (allSelected) {
                onToggle('__metro_clear__', false);
              } else {
                onToggle('__metro_all__', false);
              }
            }}
              style={{ padding: '6px 12px', borderRadius: '100px', border: `1px solid ${allSelected ? C.bk : C.g200}`, background: allSelected ? C.bk : C.white, cursor: 'pointer', ...t('13px', 500, allSelected ? C.white : C.bk), transition: 'all 0.15s' }}>
              전체
            </button>
            {region.cities.map(c => {
              const active = selected.includes(c);
              return (
                <button key={c} onClick={() => onToggle(c, true)}
                  style={{ padding: '6px 12px', borderRadius: '100px', border: `1px solid ${active ? C.bk : C.g200}`, background: active ? C.bk : C.white, cursor: 'pointer', ...t('13px', 500, active ? C.white : C.bk), transition: 'all 0.15s' }}>
                  {c}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // 도 단위: 전체 + 하위 시
  const provinceSelected = selected.includes(region.province);
  const selectedCities = region.cities.filter(c => selected.includes(`${region.province} ${c}`));
  const hasSelection = provinceSelected || selectedCities.length > 0;

  // 하위 시가 없으면 단일 토글 버튼
  if (region.cities.length === 0) {
    return (
      <button
        onClick={() => onToggle(region.province, true)}
        style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: `1px solid ${provinceSelected ? C.bk : C.g200}`, background: provinceSelected ? C.bk : C.white, cursor: 'pointer', textAlign: 'left', ...t('14px', 600, provinceSelected ? C.white : C.bk), transition: 'all 0.15s' }}
      >
        {region.province}
      </button>
    );
  }

  return (
    <div style={{ borderRadius: '10px', border: `1px solid ${hasSelection ? C.bk : C.g200}`, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(p => !p)}
        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: open ? '#f8f8f8' : C.white, border: 'none', cursor: 'pointer' }}
      >
        <span style={t('14px', 600, C.bk)}>
          {region.province}
          {hasSelection && (
            <span style={{ marginLeft: '6px', ...t('12px', 700, C.point) }}>{provinceSelected ? '전체' : selectedCities.length}</span>
          )}
        </span>
        <ChevronIcon up={open} />
      </button>
      {open && (
        <div style={{ padding: '14px', display: 'flex', flexWrap: 'wrap', gap: '6px', borderTop: `1px solid ${C.g200}` }}>
          <button onClick={() => onToggle(region.province, true)}
            style={{ padding: '6px 12px', borderRadius: '100px', border: `1px solid ${provinceSelected ? C.bk : C.g200}`, background: provinceSelected ? C.bk : C.white, cursor: 'pointer', ...t('13px', 500, provinceSelected ? C.white : C.bk), transition: 'all 0.15s' }}>
            전체
          </button>
          {region.cities.map(c => {
            const key = `${region.province} ${c}`;
            const active = selected.includes(key);
            return (
              <button key={c} onClick={() => onToggle(key, false)}
                style={{ padding: '6px 12px', borderRadius: '100px', border: `1px solid ${active ? C.bk : C.g200}`, background: active ? C.bk : C.white, cursor: 'pointer', ...t('13px', 500, active ? C.white : C.bk), transition: 'all 0.15s' }}>
                {c}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ALL_LOCATIONS = REGION_DATA.flatMap(r =>
  r.province === '광역시' ? r.cities : [r.province, ...r.cities.map(c => `${r.province} ${c}`)]
);

function FilterSidebar({ categories, locations, sort, onApply, onReset, onClose, getFilteredCount }) {
  const [tempCategories, setTempCategories] = useState(categories);
  const [tempLocations, setTempLocations] = useState(locations);
  const [tempSort, setTempSort] = useState(sort || 'latest');
  const [locationSearch, setLocationSearch] = useState('');
  const [liveCount, setLiveCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getFilteredCount(tempCategories, tempLocations).then(count => {
      if (!cancelled) setLiveCount(count);
    });
    return () => { cancelled = true; };
  }, [tempCategories, tempLocations, getFilteredCount]);

  const toggleCategory = (c) => {
    const next = tempCategories.includes(c) ? tempCategories.filter(x => x !== c) : [...tempCategories, c];
    setTempCategories(next);
  };

  const toggleLocation = (key, isAll) => {
    let next;
    if (key === '__metro_all__') {
      next = [...tempLocations.filter(x => !METRO_CITIES.includes(x)), ...METRO_CITIES];
    } else if (key === '__metro_clear__') {
      next = tempLocations.filter(x => !METRO_CITIES.includes(x));
    } else if (METRO_CITIES.includes(key)) {
      next = tempLocations.includes(key) ? tempLocations.filter(x => x !== key) : [...tempLocations, key];
    } else if (isAll) {
      const province = key;
      if (tempLocations.includes(province)) {
        next = tempLocations.filter(x => x !== province && !x.startsWith(province + ' '));
      } else {
        next = [...tempLocations.filter(x => !x.startsWith(province + ' ')), province];
      }
    } else {
      const province = key.split(' ')[0];
      next = tempLocations.includes(key) ? tempLocations.filter(x => x !== key) : [...tempLocations, key];
      next = next.filter(x => x !== province);
    }
    setTempLocations(next);
  };

  const searchResults = locationSearch.trim()
    ? ALL_LOCATIONS.filter(item => item.includes(locationSearch.trim()))
    : [];

  const handleApply = () => {
    onApply(tempCategories, tempLocations, tempSort);
    onClose();
  };

  const handleReset = () => {
    setTempCategories([]);
    setTempLocations([]);
    setTempSort('latest');
    onReset();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex' }} onClick={onClose}>
      <div style={{ flex: 1 }} />
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: '360px', background: C.white, height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}
      >
        {/* 상단 고정: 헤더 + 버튼 */}
        <div style={{ padding: '40px 32px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <span style={t('22px', 700, C.bk)}>필터</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke={C.g400} strokeWidth="2" strokeLinecap="round" /></svg>
            </button>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            <button onClick={handleReset} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', ...t('15px', 600, C.bk) }}>
              초기화
            </button>
            <button onClick={handleApply} style={{ flex: 2, padding: '12px', borderRadius: '12px', border: 'none', background: C.bk, cursor: 'pointer', ...t('15px', 600, C.white) }}>
              적용 {liveCount !== null ? `(${liveCount}건)` : ''}
            </button>
          </div>

          {/* 정렬 */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ ...t('15px', 600, C.bk), marginBottom: '10px' }}>정렬</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[{ key: 'latest', label: '최신순' }, { key: 'deadline', label: '마감 임박순' }].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setTempSort(key)}
                  style={{ flex: 1, padding: '10px', borderRadius: '10px', border: `1px solid ${tempSort === key ? C.bk : C.g200}`, background: tempSort === key ? C.bk : C.white, cursor: 'pointer', ...t('14px', 500, tempSort === key ? C.white : C.bk), transition: 'all 0.15s' }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 스크롤 가능한 필터 영역 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 32px 40px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {/* 장르 */}
          <div>
            <div style={{ ...t('15px', 600, C.bk), marginBottom: '12px' }}>장르</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {GENRES.map(c => {
                const active = tempCategories.includes(c);
                return (
                  <button key={c} onClick={() => toggleCategory(c)}
                    style={{ padding: '8px 16px', borderRadius: '100px', border: `1px solid ${active ? C.bk : C.g200}`, background: active ? C.bk : C.white, cursor: 'pointer', ...t('14px', active ? 500 : 400, active ? C.white : C.bk), transition: 'all 0.15s' }}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 지역 */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={t('15px', 600, C.bk)}>지역</span>
              {tempLocations.length > 0 && (
                <button onClick={() => setTempLocations([])} style={{ background: 'none', border: 'none', cursor: 'pointer', ...t('13px', 500, C.sub) }}>
                  초기화
                </button>
              )}
            </div>

            {/* 선택된 지역 태그 */}
            {tempLocations.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                {tempLocations.map(loc => (
                  <span key={loc} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '100px', background: C.bk, ...t('13px', 500, C.white) }}>
                    {loc}
                    <button onClick={() => {
                      const province = loc.split(' ')[0];
                      if (loc === province) {
                        setTempLocations(tempLocations.filter(x => x !== loc && !x.startsWith(loc + ' ')));
                      } else {
                        setTempLocations(tempLocations.filter(x => x !== loc));
                      }
                    }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke={C.white} strokeWidth="2" strokeLinecap="round" /></svg>
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* 지역 검색 */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ position: 'relative' }}>
                <svg style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="8" stroke={C.g300} strokeWidth="1.8" />
                  <path d="M21 21l-4.35-4.35" stroke={C.g300} strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                <input
                  value={locationSearch}
                  onChange={e => setLocationSearch(e.target.value)}
                  placeholder="지역 검색"
                  style={{ width: '100%', padding: '10px 12px 10px 36px', border: `1px solid ${C.g200}`, borderRadius: '10px', fontFamily: ff, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              {locationSearch.trim() && (
                <div style={{ marginTop: '8px', border: `1px solid ${C.g200}`, borderRadius: '10px', maxHeight: '160px', overflowY: 'auto' }}>
                  {searchResults.length > 0 ? (
                    searchResults.map(item => {
                      const active = tempLocations.includes(item);
                      const isProvince = !item.includes(' ');
                      return (
                        <button key={item} onClick={() => { toggleLocation(item, isProvince); setLocationSearch(''); }}
                          style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: active ? '#f5f5f5' : C.white, border: 'none', borderBottom: `1px solid ${C.g200}`, cursor: 'pointer', ...t('13px', 400, C.bk) }}>
                          <span>{item}</span>
                          {active
                            ? <span style={t('12px', 500, C.sub)}>선택됨</span>
                            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke={C.bk} strokeWidth="2" strokeLinecap="round" /></svg>
                          }
                        </button>
                      );
                    })
                  ) : (
                    <div style={{ padding: '14px', textAlign: 'center', ...t('13px', 400, C.sub) }}>
                      "{locationSearch.trim()}" 에 해당하는 지역이 없습니다
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 시/도 아코디언 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {REGION_DATA.map(region => (
                <RegionAccordion
                  key={region.province}
                  region={region}
                  selected={tempLocations}
                  onToggle={toggleLocation}
                  isMetro={region.province === '광역시'}
                />
              ))}
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

function AuditionsFilterBar({
  search,
  onSearchChange,
  viewMode,
  onViewModeChange,
  category,
  onApplyFilter,
  location,
  sort,
  onReset,
  getFilteredCount,
}) {
  const [inputValue, setInputValue] = useState(search);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      onSearchChange(inputValue);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const appliedCount = (category ? category.split(',').filter(Boolean).length : 0) +
    (location ? location.split(',').filter(Boolean).length : 0);

  const currentCategories = category ? category.split(',').filter(Boolean) : [];
  const currentLocations = location ? location.split(',').filter(Boolean) : [];

  return (
    <>
      <div className={styles.filterBar}>
        {/* 검색 인풋 */}
        <div className={styles.searchWrapper}>
          <svg className={styles.searchIcon} width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke={C.g300} strokeWidth="1.8" />
            <path d="M21 21l-4.35-4.35" stroke={C.g300} strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="작품명, 역할, 제작사 검색"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        </div>

        {/* 필터 버튼 */}
        <button
          className={`${styles.filterBtn} ${appliedCount > 0 ? styles.filterBtnActive : ''}`}
          onClick={() => setIsFilterOpen(true)}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M2 3h14l-5.5 6.5V15l-3-1.5V9.5L2 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          </svg>
          <span>필터{appliedCount > 0 && <span style={{ marginLeft: '6px', padding: '2px 7px', borderRadius: '100px', background: appliedCount > 0 ? '#fff' : C.bk, ...t('12px', 700, appliedCount > 0 ? C.bk : '#fff') }}>{appliedCount}</span>}</span>
        </button>

        {/* 뷰 토글 (카드형 / 리스트형) */}
        <div className={styles.viewToggle}>
          <button
            className={`${styles.viewBtn} ${viewMode === 'card' ? styles.viewBtnActive : ''}`}
            onClick={() => onViewModeChange('card')}
            aria-label="카드형 보기"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="2" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6"/>
              <rect x="11" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6"/>
              <rect x="2" y="11" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6"/>
              <rect x="11" y="11" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6"/>
            </svg>
          </button>
          <button
            className={`${styles.viewBtn} ${viewMode === 'list' ? styles.viewBtnActive : ''}`}
            onClick={() => onViewModeChange('list')}
            aria-label="리스트형 보기"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7 5h11M7 10h11M7 15h11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              <circle cx="3" cy="5" r="1.2" fill="currentColor"/>
              <circle cx="3" cy="10" r="1.2" fill="currentColor"/>
              <circle cx="3" cy="15" r="1.2" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>

      {/* 필터 사이드바 */}
      {isFilterOpen && (
        <FilterSidebar
          categories={currentCategories}
          locations={currentLocations}
          sort={sort}
          onApply={(cats, locs, s) => onApplyFilter(cats.join(','), locs.join(','), s)}
          onReset={onReset}
          onClose={() => setIsFilterOpen(false)}
          getFilteredCount={getFilteredCount}
        />
      )}
    </>
  );
}

export default AuditionsFilterBar;
